import React, { useState, useEffect } from 'react';
import './scss/Home.scss';
import { Link, useNavigate } from 'react-router-dom';
import Logo, { LogoLight } from '../components/Sidebar/Logo';
import {
    KanbanIcon,
    ListIcon,
    TeamIcon,
    SlackIcon,
    NotificationIcon,
    ImportIcon,
    TwitterIcon,
    LinkedInIcon,
    GitHubIcon
} from '../components/LandingPage/FeatureIcon';
import { useScrollAnimation, ScrollToTop } from '../components/LandingPage/ScrollAnimation';
import { useTheme } from '../context/ThemeContext';
import Toggle from '../components/Global/Toggle';
import heroScreenshot from '../assets/images/hero-screenshot.png';

// Pricing plans data from Billing.js
const plans = [
    {
        name: 'Free',
        description: 'Perfect for individuals and small teams getting started with Zoobbe.',
        prices: { monthly: '$0', annually: '$0' },
        subTexts: { monthly: 'Free forever', annually: 'Free forever' },
        features: [
            '15 collaborators',
            'Up to 15 boards',
            '512 MB storage per workspace',
            'File attachments: 20 MB per file',
            'Basic checklists',
            'Custom backgrounds & stickers',
            'Real-time notifications'
        ]
    },
    {
        name: 'Standard',
        description: 'More power for growing teams: organize, customize, and collaborate better.',
        prices: { monthly: '$4.99', annually: '$49.99' },
        subTexts: { monthly: 'Per seat / Month', annually: 'Per seat / Year' },
        features: [
            'Unlimited collaborators',
            'Unlimited boards & cards',
            'Unlimited storage',
            'File attachments: 250 MB per file',
            'Advanced checklists',
            'Saved searches & list colors',
            'Team member management',
            'Pro verified badge'
        ]
    },
    {
        name: 'Premium',
        description: 'For teams that need insights, controls, and more collaboration power.',
        prices: { monthly: '$9.99', annually: '$95.99' },
        subTexts: { monthly: 'Per seat / Month', annually: 'Per seat / Year' },
        popular: true,
        features: [
            'Everything in Standard',
            'Data export (JSON/CSV)',
            'Extended activity logs',
            'Performance insights',
            'Custom stickers & emojis',
            'White label branding',
            'Custom domain support',
            'Priority support'
        ]
    },
    {
        name: 'Enterprise',
        description: 'Enterprise-grade security, support, and scale. Tailored for large organizations.',
        prices: { monthly: 'Contact Sales', annually: 'Contact Sales' },
        subTexts: { monthly: 'Billing varies with number of users', annually: 'Billing varies with number of users' },
        features: [
            'Everything in Premium',
            'Advanced reporting',
            'User role management',
            'Organization-wide permissions',
            'SSO (Coming Soon)',
            'Custom security policies',
            'Dedicated support contact',
            'Enterprise-grade security'
        ]
    }
];

const Home = () => {
    // Initialize scroll animations
    useScrollAnimation();

    const navigate = useNavigate();
    const { theme } = useTheme();

    // Detect system theme locally for landing page (works without login)
    const [systemTheme, setSystemTheme] = useState('light');
    useEffect(() => {
        const updateSystemTheme = () => {
            const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setSystemTheme(isDarkMode ? 'dark' : 'light');
        };
        // Initial check
        updateSystemTheme();
        // Listen for changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', updateSystemTheme);
        return () => mediaQuery.removeEventListener('change', updateSystemTheme);
    }, []);

    // Pricing toggle state
    const [billingPeriod, setBillingPeriod] = useState('monthly');

    // Email signup state
    const [email, setEmail] = useState('');

    // Determine current theme
    const currentTheme = theme === 'system' ? systemTheme : theme;

    // Handle email signup
    const handleEmailSignup = (e) => {
        e.preventDefault();
        if (email) {
            // Navigate to signup page with email pre-filled
            navigate('/signup', { state: { email } });
        }
    };

    return (
        <div className="landing-page">
            <ScrollToTop />

            {/* Navigation Header */}
            <nav className="landing-nav">
                <div className="nav-container">
                    <div className="nav-logo">
                        {currentTheme === 'dark' && <Logo width="120px" />}
                        {currentTheme === 'light' && <LogoLight width="120px" />}
                    </div>
                    <div className="nav-links">
                        <a href="#features">Features</a>
                        <a href="#how-it-works">How it Works</a>
                        <a href="#pricing">Pricing</a>
                        <a href="#testimonials">Testimonials</a>
                    </div>
                    <div className="nav-actions">
                        <Link to="/login" className="nav-login">Log In</Link>
                        <Link to="/signup" className="nav-signup">Get Started Free</Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero__container">
                    <div className="hero__content">
                        <div className="hero__text">
                            <h1 className="hero__title">
                                The Future of Teamwork Lives in <span className="hero__title-accent">Zoobbe</span>
                            </h1>
                            <p className="hero__subtitle">
                                Streamline your workflow with powerful boards, seamless collaboration, and intelligent automation. Where productivity meets simplicity.
                            </p>
                            <form className="hero__signup-form" onSubmit={handleEmailSignup}>
                                <div className="hero__signup-input-group">
                                    <input
                                        type="email"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="hero__email-input"
                                        required
                                    />
                                    <button type="submit" className="hero__signup-btn">
                                        Sign Up Free
                                    </button>
                                </div>
                            </form>
                            <div className="hero__social-proof">
                                <div className="hero__avatars" aria-hidden="true">
                                    <span className="hero__avatar">JS</span>
                                    <span className="hero__avatar">MJ</span>
                                    <span className="hero__avatar">AL</span>
                                </div>
                                <p className="hero__social-text">Trusted by teams worldwide</p>
                            </div>
                            <p className="hero__note">Free forever. No credit card required.</p>
                        </div>
                        <div className="hero__image">
                            <img src={heroScreenshot} alt="Zoobbe Dashboard Screenshot" className="hero__screenshot" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features">
                <div className="container">
                    <div className="section-header">
                        <h2>Everything you need to stay organized</h2>
                        <p>Powerful features designed for modern teams</p>
                    </div>
                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-icon">
                                <KanbanIcon />
                            </div>
                            <h3>Kanban Boards</h3>
                            <p>Visualize your workflow with intuitive drag-and-drop boards that adapt to any project.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <ListIcon />
                            </div>
                            <h3>List View</h3>
                            <p>Switch to detailed list views for comprehensive project tracking and data analysis.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <TeamIcon />
                            </div>
                            <h3>Team Workspaces</h3>
                            <p>Organize projects in dedicated workspaces with granular permissions and team management.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <SlackIcon />
                            </div>
                            <h3>Slack Integration</h3>
                            <p>Stay connected with seamless Slack integration for real-time updates and notifications.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <NotificationIcon />
                            </div>
                            <h3>Smart Notifications</h3>
                            <p>Get notified about what matters most with intelligent, customizable notification settings.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <ImportIcon />
                            </div>
                            <h3>Trello Import</h3>
                            <p>Migrate from Trello effortlessly with our one-click import tool. Keep your workflow intact.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it Works Section */}
            <section id="how-it-works" className="how-it-works">
                <div className="container">
                    <div className="section-header">
                        <h2>How Zoobbe Works</h2>
                        <p>Get started in minutes, not hours</p>
                    </div>
                    <div className="steps-grid">
                        <div className="step">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Create Your Board</h3>
                                <p>Start with a template or build from scratch. Customize columns, labels, and workflows to match your team's needs.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Invite Your Team</h3>
                                <p>Add team members, set permissions, and create workspaces. Collaboration starts with the right people in the right place.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Stay Organized</h3>
                                <p>Track progress, manage deadlines, and keep everyone aligned with real-time updates and smart notifications.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="pricing">
                <div className="container">
                    <div className="section-header">
                        <h2>Simple, Transparent Pricing</h2>
                        <p>Choose the plan that fits your team's needs</p>
                    </div>

                    {/* Billing Toggle */}
                    <div className="billing-toggle-container">
                        <div className="billing-toggle-wrapper">
                            <span className={`billing-period-label ${billingPeriod === 'monthly' ? 'active' : ''}`}>
                                Monthly
                            </span>
                            <Toggle
                                checked={billingPeriod === 'annually'}
                                onChange={(checked) => setBillingPeriod(checked ? 'annually' : 'monthly')}
                                size="md"
                            />
                            <span className={`billing-period-label ${billingPeriod === 'annually' ? 'active' : ''}`}>
                                Annually <span className="discount">SAVE 17%</span>
                            </span>
                        </div>
                    </div>

                    <div className="pricing-grid">
                        {plans.map((plan) => {
                            const isPopular = plan.popular;
                            const price = plan.prices[billingPeriod];
                            const subText = plan.subTexts[billingPeriod];

                            return (
                                <div
                                    key={plan.name}
                                    className={`pricing-card ${plan.name.toLowerCase()} ${isPopular ? 'featured' : ''}`}
                                >
                                    {isPopular && <div className="popular-badge">Most Popular</div>}

                                    <div className="pricing-header">
                                        <h3>{plan.name}</h3>
                                        <div className="price">
                                            {price.includes('$') ? (
                                                <>
                                                    <span className="currency">$</span>
                                                    <span className="amount">{price.replace('$', '')}</span>
                                                    <span className="period"> USD</span>
                                                </>
                                            ) : (
                                                <span className="contact-sales">{price}</span>
                                            )}
                                        </div>
                                        <div className="price-subtitle">{subText}</div>
                                    </div>

                                    <div className="plan-description">{plan.description}</div>

                                    <ul className="features-list">
                                        {plan.features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>

                                    {plan.name === 'Enterprise' ? (
                                        <a href="mailto:sales@zoobbe.com" className="pricing-cta">Contact Sales</a>
                                    ) : plan.name === 'Free' ? (
                                        <Link to="/signup" className="pricing-cta">Get Started Free</Link>
                                    ) : (
                                        <Link to="/signup" className="pricing-cta">Start Free Trial</Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className="testimonials">
                <div className="container">
                    <div className="section-header">
                        <h2>Trusted by Teams Worldwide</h2>
                        <p>See what our users have to say</p>
                    </div>
                    <div className="testimonials-grid">
                        <div className="testimonial-card">
                            <div className="testimonial-content">
                                <p>"Zoobbe transformed how our team collaborates. The intuitive interface and powerful features make project management effortless."</p>
                            </div>
                            <div className="testimonial-author">
                                <div className="author-avatar">
                                    <span>JS</span>
                                </div>
                                <div className="author-info">
                                    <h4>Jane Smith</h4>
                                    <p>Product Manager</p>
                                </div>
                            </div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-content">
                                <p>"The Slack integration is a game-changer. We stay updated without switching between apps. Highly recommend!"</p>
                            </div>
                            <div className="testimonial-author">
                                <div className="author-avatar">
                                    <span>MJ</span>
                                </div>
                                <div className="author-info">
                                    <h4>Mike Johnson</h4>
                                    <p>Engineering Lead</p>
                                </div>
                            </div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-content">
                                <p>"Migrating from Trello was seamless. Zoobbe offers everything we loved about Trello, plus so much more."</p>
                            </div>
                            <div className="testimonial-author">
                                <div className="author-avatar">
                                    <span>AL</span>
                                </div>
                                <div className="author-info">
                                    <h4>Alex Lee</h4>
                                    <p>Startup Founder</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Transform Your Workflow?</h2>
                        <p>Join thousands of teams already using Zoobbe to stay organized and productive.</p>
                        <div className="cta-actions">
                            <Link to="/signup" className="cta-button primary">Get Started Free</Link>
                            <a href="#how-it-works" className="cta-button secondary">Watch Demo</a>
                        </div>
                        <p className="cta-note">No credit card required • Free forever plan available</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-section">
                            <div className="footer-logo">
                                {currentTheme === 'dark' && <Logo width="120px" />}
                                {currentTheme === 'light' && <LogoLight width="120px" />}
                            </div>
                            <p>The future of teamwork lives in Zoobbe. Streamline your workflow with powerful boards and seamless collaboration.</p>
                        </div>
                        <div className="footer-section">
                            <h4>Product</h4>
                            <ul>
                                <li><a href="#features">Features</a></li>
                                <li><a href="#pricing">Pricing</a></li>
                            </ul>
                        </div>
                        <div className="footer-section">
                            <h4>Company</h4>
                            <ul>
                                <li><Link to="/about">About</Link></li>
                                <li><Link to="/contact">Contact</Link></li>
                            </ul>
                        </div>
                        <div className="footer-section">
                            <h4>Support</h4>
                            <ul>
                                <li><Link to="/help">Help Center</Link></li>
                            </ul>
                        </div>
                        <div className="footer-section">
                            <h4>Legal</h4>
                            <ul>
                                <li><Link to="/legal/terms-of-service">Terms of Service</Link></li>
                                <li><Link to="/legal/privacy-policy">Privacy Policy</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>&copy; 2024 Zoobbe. All rights reserved.</p>
                        <div className="footer-social">
                            <a href="https://twitter.com/zoobbe" target="_blank" rel="noopener noreferrer" aria-label="Follow Zoobbe on Twitter">
                                <TwitterIcon />
                            </a>
                            <a href="https://linkedin.com/company/zoobbe" target="_blank" rel="noopener noreferrer" aria-label="Follow Zoobbe on LinkedIn">
                                <LinkedInIcon />
                            </a>
                            <a href="https://github.com/zoobbe" target="_blank" rel="noopener noreferrer" aria-label="View Zoobbe on GitHub">
                                <GitHubIcon />
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;
